"use client";

import { forwardRef } from "react";
import { Box, Typography, styled } from "@mui/material";
import { Avatar, Button, Checkbox } from "@/ui";
import { ArrowUpDown } from "lucide-react";
import EditButton from "./EditButton";
import MarkdownList from "./MarkdownList";
import HiddenChip from "@components/Floats/Create/Steps/parts/HiddenChip";

const Panel = styled(Box, {
  shouldForwardProp: (prop) => prop !== "selected" && prop !== "flat" && prop !== "compact",
})(({ selected, flat, compact, theme }) => ({
  display: "flex",
  gap: "1rem",
  padding: flat ? "24px 0" : compact ? "16px" : "28px 16px",
  width: "100%",
  cursor: flat ? "default" : "pointer",
  position: "relative",
  borderRadius: "var(--radius-md, 8px)",
  backgroundColor: flat
    ? "transparent"
    : selected
      ? theme.palette.primary.lighter
      : "transparent",
  border: flat
    ? "none"
    : `1px solid ${selected ? "transparent" : theme.palette.border}`,
  transition: "background-color 0.4s",

  "& .fade": {
    opacity: flat || selected ? 1 : 0.65,
  },
}));

const hasVisibleContent = (content) => {
  if (typeof content !== "string") return Boolean(content);
  return content
    .replace(/<[^>]*>/g, "")
    .replace(/&nbsp;|&#160;/g, " ")
    .trim().length > 0;
};

// Shared panel for a single experience/role item — used by CandidateRoles.js
// (Floats create flow) and documented live in the styleguide, so both stay
// visually identical automatically.
const ExperienceCard = forwardRef(function ExperienceCard(
  /**
   * @type {{
   *   selected?: boolean; style?: React.CSSProperties; onClick?: () => void;
   *   logoSrc?: string; title?: string; subtitle?: string; dateRange?: string;
   *   summary?: string; checked?: boolean; onCheckedChange?: (checked: boolean) => void;
   *   onReorderClick?: () => void; reorderLabel?: string; onEdit?: () => void;
   *   hideCheckbox?: boolean; avatarBg?: string; flat?: boolean; compact?: boolean;
   *   avatarVariant?: "circle" | "rounded" | "square"; avatarIcon?: React.ReactNode;
   * }}
   */
  {
    selected,
    style,
    onClick,
    logoSrc,
    title,
    subtitle,
    dateRange,
    summary,
    checked,
    onCheckedChange,
    onReorderClick,
    reorderLabel = "Re-order roles",
    onEdit,
    hideCheckbox = false,
    avatarBg,
    flat = false,
    // Smaller type and padding, for lists of simple items (e.g. portfolio).
    compact = false,
    // Shape of the logo/thumbnail; "rounded" = square with rounded corners.
    avatarVariant = "circle",
    // An icon tile shown instead of the logo/initials (e.g. a file icon).
    avatarIcon,
  },
  ref
) {
  const singleLine = !subtitle && !dateRange;
  const hasSummary = hasVisibleContent(summary);

  return (
    <Panel ref={ref} style={style} selected={selected} flat={flat} compact={compact} onClick={onClick}>
      <Box
        className="fade"
        sx={{ flex: 1, minWidth: 0, display: "flex", flexDirection: "column", gap: "1rem" }}
      >
        {!selected && !flat && <HiddenChip />}
        <Box sx={{ display: "flex", alignItems: singleLine ? "center" : "flex-start", gap: 1.5 }}>
          {!hideCheckbox && (
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                width: "36px",
                flexShrink: 0,
                mt: singleLine ? 0 : "10px",
              }}
              onClick={(event) => event.stopPropagation()}
            >
              <Checkbox checked={checked} onChange={onCheckedChange} />
            </Box>
          )}
          {avatarIcon ? (
            <Box
              aria-hidden="true"
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                width: "40px",
                height: "40px",
                flexShrink: 0,
                borderRadius: avatarVariant === "circle" ? "9999px" : avatarVariant === "square" ? 0 : "var(--radius-md)",
                boxSizing: "border-box",
                border: "1px solid var(--text-primary, #131313)",
                backgroundColor: "var(--bg-muted, #f1f3f8)",
                color: "var(--text-primary)",
              }}
            >
              {avatarIcon}
            </Box>
          ) : (
            <Avatar
              src={logoSrc}
              name={title}
              alt={title}
              size="md"
              variant={avatarVariant}
              fallbackBg={avatarBg}
              style={{ flexShrink: 0 }}
            />
          )}
          <Box sx={{ display: "flex", flexDirection: "column", gap: "var(--space-2, 8px)", minWidth: 0 }}>
            <Typography
              variant={compact ? undefined : "h3"}
              sx={compact ? { font: "var(--text-subtitle1)", fontWeight: 600, margin: 0 } : { margin: 0 }}
            >
              {title}
            </Typography>
            {subtitle && (
              <Typography
                className="fade"
                // Compact cards show a URL or file name: keep it to one line.
                title={compact && typeof subtitle === "string" ? subtitle : undefined}
                sx={
                  compact
                    ? {
                        font: "var(--text-body3)",
                        color: "text.secondary",
                        margin: 0,
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        whiteSpace: "nowrap",
                      }
                    : { font: "var(--text-subtitle1)", margin: 0 }
                }
              >
                {subtitle}
              </Typography>
            )}
            {dateRange && (
              <Typography className="fade" sx={{ font: "var(--text-body2)", color: "#555555", margin: 0 }}>
                {dateRange}
              </Typography>
            )}
          </Box>
        </Box>
        {hasSummary && (
          <Box
            sx={{
              // Aligns the summary's left edge with the title: avatar (40px) + gap
              // (12px), plus the checkbox (36px) + its own gap (12px) when shown.
              ml: hideCheckbox ? "52px" : "100px",
              "& p:first-child, & ul:first-child, & ol:first-child": { mt: 0 },
              "& p:last-child, & ul:last-child, & ol:last-child": { mb: 0 },
            }}
          >
            <MarkdownList content={summary} />
          </Box>
        )}
      </Box>
      {selected && (onReorderClick || onEdit) && (
        <Box
          sx={{
            flexShrink: 0,
            display: "flex",
            flexDirection: "column",
            alignItems: "flex-end",
            gap: "6px",
          }}
        >
          {onReorderClick && (
            <Button
              variant="tertiary"
              color="pink"
              size="sm"
              noPadding
              endIcon={<ArrowUpDown size={12} />}
              onClick={(event) => {
                event.stopPropagation();
                onReorderClick();
              }}
              style={{ whiteSpace: "nowrap" }}
            >
              {reorderLabel}
            </Button>
          )}
          {onEdit && (
            <EditButton onEdit={onEdit} sx={{ position: "static", top: "auto", right: "auto" }} />
          )}
        </Box>
      )}
    </Panel>
  );
});

export default ExperienceCard;
